import React, { useCallback, useMemo, useState } from 'react'
import { verifyWebViewRender, webview } from '../../utils/verifyWebViewRender'
import { fetchReceiptContent } from '../../services/receipt-print.service.ts'
import { showToast } from '../sonner.tsx'

interface BaseData {
  [key: string]: any
}

type ReceiptType = 'collection-order' | 'journey' | 'daily-sales'

interface PrintReceiptModalProps {
  isOpen: boolean
  onClose: () => void
  type: ReceiptType
  /** Dados básicos (collection-order/journey) OU filtros/pagination no caso de daily-sales */
  data?: BaseData | null
  /** Permite sobrescrever a busca de conteúdo. Recebe (mode) e deve retornar string do conteúdo. */
  fetchOverride?: (
    mode: 'pdf' | 'zpl',
    type: ReceiptType,
    data?: BaseData | null,
  ) => Promise<string>
}

// Util simples para codificar em Base64 (UTF-8 safe)
function encodeBase64(raw: string) {
  return btoa(decodeURIComponent(encodeURIComponent(raw)))
}

export const PrintReceiptModal: React.FC<PrintReceiptModalProps> = ({
  isOpen,
  onClose,
  type,
  data,
  fetchOverride,
}) => {
  // Estados para controle de requisição ao backend
  const [loading, setLoading] = useState<'zebra' | 'pdf' | null>(null)

  // Obter ID dependendo do tipo
  const resourceId = useMemo(() => {
    if (!data) return null
    if (type === 'daily-sales') return 'daily-sales-filters' // marcador simbólico
    if (type === 'collection-order') {
      return data.CollectionOrderId || data.collectionOrderId || data.id || null
    }
    // journey
    return data.journeyId || data.journey?.journeyId || data.id || null
  }, [data, type])

  // Para daily-sales permitiremos enviar se houver data (filtros) mesmo sem id numérico
  const canSend = type === 'daily-sales' ? !!data : !!resourceId

  const sendToFlutter = useCallback(
    async (handler: string, base64Payload: string) => {
      // Tenta diferentes formas de acessar o handler (ambiente WebView Flutter / fallback)
      const tryCall = async (): Promise<any> => {
        if (verifyWebViewRender() && webview?.callHandler) {
          return await webview.callHandler(handler, base64Payload)
        }
        if (webview?.callHandler) {
          return await webview.callHandler(handler, base64Payload)
        }
        const w = (window as any).webview
        if (w?.callHandler) return await w.callHandler(handler, base64Payload)
        // Se realmente não existir handler, simulamos sucesso para não quebrar uso web puro
        return { status: 'noop', message: 'Error de Comunicación' }
      }
      try {
        return await tryCall()
      } catch (err) {
        // Última tentativa direta usando window
        try {
          const w = (window as any).webview
          if (w?.callHandler) return await w.callHandler(handler, base64Payload)
        } catch (_) {
          /* ignora */
        }
        throw err
      }
    },
    [],
  )

  // Handler para clique dos botões (pdf/zpl)
  const handleFetchAndSend = useCallback(
    async (mode: 'pdf' | 'zpl') => {
      if (!canSend || !resourceId) return
      setLoading(mode === 'pdf' ? 'pdf' : 'zebra')
      try {
        const content = fetchOverride
          ? await fetchOverride(mode, type, data)
          : await fetchReceiptContent(
              type as 'collection-order' | 'journey',
              resourceId,
              mode,
            )
        const base64Payload = encodeBase64(content || '')
        const handler =
          mode === 'zpl'
            ? 'onConvertHTMLAndPrintZebraReceipt'
            : 'onConvertHTMLAndDownloadPDFReceipt'

        const response = await sendToFlutter(handler, base64Payload)

        // Heurística de erro: objeto com chave message e sem status ok/noop
        const isError =
          response &&
          typeof response === 'object' &&
          'message' in response &&
          !['ok', 'noop'].includes((response as any).status)

        if (isError) {
          showToast(
            (response as any).message ||
              (mode === 'zpl'
                ? 'Fallo al imprimir en la impresora Zebra.'
                : 'Fallo al generar el PDF.'),
            'error',
          )
        } else {
          showToast(
            mode === 'zpl'
              ? 'Comprobante enviado para impresora Zebra.'
              : 'Comprobante enviado para generación de PDF.',
            'success',
          )
        }
      } catch (err: any) {
        showToast(
          err?.message || 'Fallo al obtener el comprobante desde el servidor.',
          'error',
        )
      } finally {
        setLoading(null)
      }
    },
    [canSend, onClose, resourceId, sendToFlutter, type],
  )

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="text-lg font-semibold text-gray-800">
            {type === 'collection-order'
              ? 'Impresión / Exportación de Cobro'
              : 'Impresión / Exportación de Jornada'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 transition hover:text-gray-700"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>
        <div className="space-y-4 px-4 py-5 text-sm text-gray-700">
          {!canSend && (
            <div className="rounded bg-yellow-50 p-3 text-yellow-700">
              ID no disponible para solicitar el comprobante.
            </div>
          )}
          <p className="leading-relaxed">
            Seleccione una opción para imprimir el comprobante utilizando la
            impresora Zebra o exportar el PDF.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <button
              disabled={!canSend || !!loading}
              onClick={() => handleFetchAndSend('zpl')}
              className="relative flex-1 rounded-md bg-indigo-600 px-4 py-2 text-center text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading === 'zebra' && (
                <span className="absolute left-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              Imprimir en Zebra
            </button>
            <button
              disabled={!canSend || !!loading}
              onClick={() => handleFetchAndSend('pdf')}
              className="relative flex-1 rounded-md bg-emerald-600 px-4 py-2 text-center text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading === 'pdf' && (
                <span className="absolute left-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              Exportar PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PrintReceiptModal
